import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ChevronRight, Plus } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import NotFoundPage from './NotFoundPage';
import { ServiceIcon, servicePath } from '../components/ServiceCard';
import { useServices, localized, localizedList } from '../hooks/useServices';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useService(slug) {
  const services = useServices();
  const [service, setService] = useState(undefined);
  useEffect(() => {
    setService(undefined);
    axios.get(`${API}/services/${slug}`).then(r => setService(r.data)).catch(() => setService(null));
  }, [slug]);
  const cached = services.find(s => (s.slug || s.key) === slug);
  return { service: service === undefined ? cached : service, services };
}

function Hero({ service }) {
  const { t, language, lp } = useLanguage();
  const h1 = localized(service, 'h1', language) || localized(service, 'title', language);
  return (
    <section className="relative bg-[#0F172A] overflow-hidden" data-testid="service-hero">
      <div className="absolute inset-0 opacity-[0.06]" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '36px 36px'}} />
      <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div className="animate-fadeInUp" style={{ opacity: 0, animationFillMode: 'forwards' }}>
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-sm text-slate-400 mb-10" data-testid="service-breadcrumb">
            <Link to={lp('/')} className="hover:text-white transition-colors">{t('nav.home')}</Link>
            <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
            <Link to={lp('/leistungen')} className="hover:text-white transition-colors">{t('nav.services')}</Link>
            <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
            <span className="text-slate-200" aria-current="page">{localized(service, 'title', language)}</span>
          </nav>
          <div className="w-14 h-14 rounded-xl bg-[#066ce7] text-white flex items-center justify-center mb-6">
            <ServiceIcon name={service.icon} className="w-7 h-7" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] mb-4" data-testid="service-detail-title">{h1}</h1>
          <p className="text-[#3b8dff] text-lg md:text-xl font-semibold mb-6" data-testid="service-detail-subtitle">{localized(service, 'subtitle', language)}</p>
          <p className="text-slate-300 leading-relaxed mb-10 max-w-xl">{localized(service, 'description', language)}</p>
          <div className="flex flex-wrap gap-4">
            <Link to={lp('/contact')}>
              <Button className="bg-[#066ce7] hover:bg-[#005bb5] text-white px-7 h-12 text-base font-semibold" data-testid="service-hero-contact">
                {t('service.ctaBtn')} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to={lp('/planner')}>
              <Button variant="outline" className="border-2 border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white px-7 h-12 text-base font-semibold" data-testid="service-hero-planner">
                {t('hero.cta1')}
              </Button>
            </Link>
          </div>
        </div>
        <div className="relative animate-fadeInUp" style={{ animationDelay: '0.15s', opacity: 0, animationFillMode: 'forwards' }}>
          <img src={service.image || '/api/uploads/hero_main.jpg'} alt={h1} width="1200" height="800" fetchPriority="high" className="w-full h-[300px] md:h-[440px] object-cover rounded-2xl shadow-2xl" />
          <div className="absolute -bottom-5 left-6 right-6 sm:right-auto bg-white rounded-xl px-5 py-4 shadow-xl border-l-4 border-[#066ce7]">
            <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">ITCS Industrial</p>
            <p className="font-bold text-[#0F172A]">{localized(service, 'title', language)}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Overview({ service }) {
  const { t, language } = useLanguage();
  return (
    <section className="py-20 md:py-28 blueprint-bg" data-testid="service-overview">
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <span className="inline-block text-[#066ce7] text-sm font-semibold uppercase tracking-wider mb-3">{localized(service, 'title', language)}</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-4">{t('service.overview')}</h2>
          <p className="text-slate-600 leading-relaxed">{t('service.overviewText')}</p>
        </div>
        <ul className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
          {localizedList(service, 'bullets', language).map((b, i) => (
            <li key={i} className="group flex items-start gap-4 bg-white border border-slate-200 rounded-xl p-5 transition-[border-color,box-shadow] duration-300 hover:border-[#066ce7]/60 hover:shadow-lg" data-testid={`service-bullet-${i}`}>
              <span className="w-9 h-9 rounded-lg bg-blue-50 text-[#066ce7] flex items-center justify-center shrink-0 transition-colors group-hover:bg-[#066ce7] group-hover:text-white">
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} aria-hidden="true" />
              </span>
              <span className="text-[#0F172A] font-medium pt-1.5">{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Process({ service }) {
  const { t, language } = useLanguage();
  const steps = localizedList(service, 'process', language);
  if (!steps.length) return null;
  return (
    <section className="py-20 md:py-28 bg-white" data-testid="service-process">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-14">{t('service.process')}</h2>
        <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((s, i) => (
            <li key={i} className="relative pt-8 border-t-2 border-slate-100 hover:border-[#066ce7] transition-colors duration-300" data-testid={`service-step-${i}`}>
              <span className="font-mono text-sm text-[#066ce7] block mb-3">{String(i + 1).padStart(2, '0')}</span>
              <h3 className="text-xl font-bold text-[#0F172A] mb-2">{s.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{s.text}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function FaqSection({ faqs, testid = 'service-faq' }) {
  const { t } = useLanguage();
  if (!faqs.length) return null;
  return (
    <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-100" data-testid={testid}>
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-10">{t('service.faq')}</h2>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <details key={i} className="group bg-white border border-slate-200 rounded-xl px-6 open:border-[#066ce7] transition-colors" data-testid={`${testid}-${i}`}>
              <summary className="flex items-center justify-between gap-4 cursor-pointer list-none py-5 font-semibold text-[#0F172A] hover:text-[#066ce7] [&::-webkit-details-marker]:hidden">
                <h3 className="text-base font-semibold">{f.q}</h3>
                <Plus className="w-5 h-5 shrink-0 text-[#066ce7] transition-transform duration-300 group-open:rotate-45" aria-hidden="true" />
              </summary>
              <p className="text-slate-600 leading-relaxed pb-5">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function Related({ service, services }) {
  const { t, language, lp } = useLanguage();
  const others = services.filter(s => s.key !== service.key);
  if (!others.length) return null;
  return (
    <section className="py-20 md:py-28 bg-white" data-testid="service-related">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-10">{t('service.related')}</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {others.map(s => (
            <Link key={s.key} to={lp(servicePath(s))} className="group flex items-start gap-4 border border-slate-200 rounded-2xl p-6 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 hover:border-[#066ce7] hover:shadow-xl" data-testid={`related-service-${s.key}`}>
              <span className="w-12 h-12 rounded-xl bg-blue-50 text-[#066ce7] flex items-center justify-center shrink-0 transition-colors group-hover:bg-[#066ce7] group-hover:text-white">
                <ServiceIcon name={s.icon} className="w-6 h-6" />
              </span>
              <span className="flex-1">
                <span className="block text-lg font-bold text-[#0F172A]">{localized(s, 'title', language)}</span>
                <span className="block text-sm text-[#066ce7] font-medium mt-1">{localized(s, 'subtitle', language)}</span>
              </span>
              <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-[#066ce7] transition-colors mt-1" aria-hidden="true" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Cta({ testid = 'service-cta' }) {
  const { t, lp } = useLanguage();
  return (
    <section className="py-20 md:py-24 bg-[#066ce7] relative overflow-hidden" data-testid={testid}>
      <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '30px 30px'}} />
      <div className="relative max-w-7xl mx-auto px-4 md:px-8 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
        <div className="max-w-2xl">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">{t('service.ctaTitle')}</h2>
          <p className="text-blue-100 text-lg">{t('service.ctaText')}</p>
        </div>
        <Link to={lp('/contact')}>
          <Button className="bg-white text-[#066ce7] hover:bg-blue-50 px-8 h-12 text-base font-semibold shadow-xl" data-testid={`${testid}-btn`}>
            {t('service.ctaBtn')} <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </Link>
      </div>
    </section>
  );
}

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const { language } = useLanguage();
  const { service, services } = useService(slug);

  if (service === null) return <NotFoundPage />;
  if (!service) return <div className="min-h-[70vh] bg-[#0F172A]" data-testid="service-detail-loading"><SEO /></div>;

  return (
    <div data-testid="service-detail-page">
      <SEO />
      <Hero service={service} />
      <Overview service={service} />
      <Process service={service} />
      <FaqSection faqs={localizedList(service, 'faqs', language)} />
      <Related service={service} services={services} />
      <Cta />
    </div>
  );
}
